/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file procurementApi.js
 * @description
 * Networking layer of the mobile **Beszerzés (procurement)** module — the
 * React-Native counterpart of the admin's `src/networking/procurementNetworking.js`
 * and `procurementSourcingNetworking.js`, rewritten on this app's own
 * {@link module:helpers/WorkSheetContext} `apiFetch` transport (bearer token +
 * demo-mode proxy + GET dedup) instead of `window.swaggerClient`.
 *
 * Every path here is a REAL backend route from
 * `HappyPOS-backend/src/api/swagger/procurement.swagger.yaml` (tag `Procurement`,
 * 34 operations) and `procurementSourcing.swagger.yaml` (20 operations); nothing
 * is mocked and no endpoint is invented — the mobile module drives exactly the
 * same API the web admin does, so a requisition raised on a phone is the same
 * document an approver sees in the admin.
 *
 * Two conventions worth knowing before adding a call:
 *
 *  - **Envelope.** The procurement controllers answer `{success, message, data}`
 *    (`ok()`/`fail()` in `procurement.controller.js`), NOT the bare bodies the
 *    older management endpoints return. Everything therefore goes through
 *    {@link unwrapData}.
 *  - **Scope.** The store is resolved from `restaurantId` (query param on reads,
 *    body field on creates) plus the `x-restaurant-id` header, exactly like
 *    `managementApi.js` — a multi-store manager who picked store B must not get
 *    store A's purchase orders back.
 *
 * @module helpers/procurementApi
 */

import {apiFetch} from './WorkSheetContext';
import {getManagementScope} from './managementApi';

/* -------------------------------------------------------------------------- */
/*  Transport helpers                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Unwrap the `{success, message, data}` envelope the procurement controllers
 * answer with. Degrades to the raw body when a route ever answers bare, so a
 * caller never has to guard the shape itself.
 * @param {*} response Parsed response body.
 * @returns {*} The `data` payload, or the response itself.
 */
export const unwrapData = (response) => (
    response && typeof response === 'object' && response.data !== undefined ? response.data : response
);

/**
 * Coerce an unwrapped payload into an array (list endpoints answer either a
 * bare array or an object holding one).
 * @param {*} value Unwrapped payload.
 * @param {string[]} [keys] Object keys to try before giving up.
 * @returns {Array} Always an array.
 */
const asList = (value, keys = []) => {
    if (Array.isArray(value)) {
        return value;
    }
    for (const key of keys) {
        if (Array.isArray(value?.[key])) {
            return value[key];
        }
    }

    return [];
};

/**
 * Build a query string from a params object, dropping empty values.
 * @param {object} [params] Query params.
 * @returns {string} `?a=1&b=2`, or `''` when nothing survives.
 */
const qs = (params = {}) => {
    const query = Object.entries(params)
        .filter(([, value]) => value !== undefined && value !== null && value !== '')
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
        .join('&');

    return query ? `?${query}` : '';
};

/**
 * Scope headers for a procurement call. Mirrors `managementApi.scopeHeaders`:
 * several backend resolvers fall back to the caller's PRIMARY store when the
 * header is absent, which would silently show the wrong store's documents.
 * @param {{restaurantId?: string}} [scope] Active management scope.
 * @returns {Record<string, string>} Headers to merge into the fetch options.
 */
const scopeHeaders = (scope) => (scope?.restaurantId ? {'x-restaurant-id': String(scope.restaurantId)} : {});

/**
 * GET a procurement route and unwrap its envelope.
 * @param {string} path Path after `/api`.
 * @param {{restaurantId?: string}} [scope] Active scope (for the header).
 * @returns {Promise<*>} Unwrapped payload.
 */
const get = async (path, scope) => unwrapData(await apiFetch(path, {headers: scopeHeaders(scope)}));

/**
 * Send a body-carrying request (POST/PUT/DELETE) and unwrap its envelope.
 * @param {string} method HTTP method.
 * @param {string} path Path after `/api`.
 * @param {object|null} [body] JSON body (omitted when null/undefined).
 * @param {{restaurantId?: string}} [scope] Active scope (for the header).
 * @returns {Promise<*>} Unwrapped payload.
 */
const send = async (method, path, body, scope) => unwrapData(await apiFetch(path, {
    method,
    headers: {'Content-Type': 'application/json', ...scopeHeaders(scope)},
    ...(body === undefined || body === null ? {} : {body: JSON.stringify(body)}),
}));

/**
 * Resolve the scope once for a screen: the caller may pass one in, otherwise it
 * is read from storage (same source the rest of the management UI uses).
 * @param {object} [scope] Optional pre-resolved scope.
 * @returns {Promise<object>} The management scope.
 */
export const resolveProcurementScope = async (scope) => scope || getManagementScope();

/* -------------------------------------------------------------------------- */
/*  Module settings                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Load the store's procurement settings (approval threshold + approvers,
 * 3-way-match tolerances, receipt/expense automation, auto-reorder block).
 * @param {{restaurantId?: string}} scope Active management scope.
 * @returns {Promise<object|null>} The settings document (defaults backfilled).
 */
export const fetchProcurementSettings = async (scope) => (
    scope?.restaurantId
        ? (await get(`/procurement/settings${qs({restaurantId: scope.restaurantId})}`, scope)) || null
        : null
);

/**
 * Persist a partial settings patch. Nested `autoReorder` keys are accepted as a
 * nested object — the controller flattens them into a dot-path `$set`.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {object} payload Fields to update.
 * @returns {Promise<object>} The saved settings document.
 */
export const updateProcurementSettings = async (scope, payload) => send(
    'PUT',
    `/procurement/settings${qs({restaurantId: scope?.restaurantId})}`,
    payload,
    scope,
);

/* -------------------------------------------------------------------------- */
/*  Dashboard                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Procurement hub payload: per-status counts for orders / requisitions / RFQs,
 * the open-order net value, the caller's own pending approvals and the newest
 * orders.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @returns {Promise<object|null>}
 */
export const fetchProcurementDashboard = async (scope) => (
    scope?.restaurantId
        ? (await get(`/procurement/dashboard${qs({restaurantId: scope.restaurantId})}`, scope)) || null
        : null
);

/* -------------------------------------------------------------------------- */
/*  Purchase requisitions (Igénylések)                                         */
/* -------------------------------------------------------------------------- */

/**
 * List the store's purchase requisitions, newest first.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {{status?: string, search?: string, limit?: number}} [options] Filters.
 * @returns {Promise<Array>} Requisitions (never null).
 */
export const fetchRequisitions = async (scope, options = {}) => {
    if (!scope?.restaurantId) {
        return [];
    }

    return asList(await get(`/procurement/requisitions${qs({
        restaurantId: scope.restaurantId,
        status: options.status,
        search: options.search,
        limit: options.limit || 200,
    })}`, scope), ['requisitions']);
};

/**
 * Load one requisition with its requester / warehouse / item catalog refs and
 * approval metadata populated.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {string} requisitionId Requisition `_id`.
 * @returns {Promise<object|null>}
 */
export const fetchRequisition = async (scope, requisitionId) => (
    (await get(`/procurement/requisitions/${encodeURIComponent(requisitionId)}`, scope)) || null
);

/**
 * Create a draft requisition ("Új igénylés"). At least one line is required.
 * @param {{restaurantId?: string, placeId?: string}} scope Active management scope.
 * @param {object} payload `{items, warehouse, neededByDate, note, place}`.
 * @returns {Promise<object>} The created requisition.
 */
export const createRequisition = async (scope, payload) => send('POST', '/procurement/requisitions', {
    restaurantId: scope?.restaurantId,
    ...(scope?.placeId ? {place: scope.placeId} : {}),
    ...payload,
}, scope);

/**
 * Update a `draft`/`rejected` requisition (409 in any other status).
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {string} requisitionId Requisition `_id`.
 * @param {object} payload Fields to change.
 * @returns {Promise<object>}
 */
export const updateRequisition = async (scope, requisitionId, payload) => send(
    'PUT',
    `/procurement/requisitions/${encodeURIComponent(requisitionId)}`,
    payload,
    scope,
);

/**
 * Soft-delete a `draft`/`rejected`/`cancelled` requisition.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {string} requisitionId Requisition `_id`.
 * @returns {Promise<object>}
 */
export const deleteRequisition = async (scope, requisitionId) => send(
    'DELETE',
    `/procurement/requisitions/${encodeURIComponent(requisitionId)}`,
    null,
    scope,
);

/**
 * Submit a draft requisition for decision (notifies the store's approvers).
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {string} requisitionId Requisition `_id`.
 * @returns {Promise<object>}
 */
export const submitRequisition = async (scope, requisitionId) => send(
    'POST',
    `/procurement/requisitions/${encodeURIComponent(requisitionId)}/submit`,
    {},
    scope,
);

/**
 * Approve or reject a submitted requisition.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {string} requisitionId Requisition `_id`.
 * @param {'approved'|'rejected'} decision The decision to record.
 * @param {string} [note] Optional decision note shown to the requester.
 * @returns {Promise<object>}
 */
export const decideRequisition = async (scope, requisitionId, decision, note = '') => send(
    'POST',
    `/procurement/requisitions/${encodeURIComponent(requisitionId)}/decide`,
    {decision, note},
    scope,
);

/**
 * Convert an approved requisition into an RFQ or a purchase order.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {string} requisitionId Requisition `_id`.
 * @param {'rfq'|'purchaseOrder'} target What to create.
 * @param {string} [partnerId] Supplier (required by the `purchaseOrder` target).
 * @returns {Promise<object>} The created RFQ / purchase order.
 */
export const convertRequisition = async (scope, requisitionId, target, partnerId) => send(
    'POST',
    `/procurement/requisitions/${encodeURIComponent(requisitionId)}/convert`,
    {target, ...(partnerId ? {partnerId} : {})},
    scope,
);

/* -------------------------------------------------------------------------- */
/*  Requests for quote (Ajánlatkérések)                                        */
/* -------------------------------------------------------------------------- */

/**
 * List the store's RFQs, newest first.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {{status?: string, search?: string, limit?: number}} [options] Filters.
 * @returns {Promise<Array>}
 */
export const fetchRfqs = async (scope, options = {}) => {
    if (!scope?.restaurantId) {
        return [];
    }

    return asList(await get(`/procurement/rfqs${qs({
        restaurantId: scope.restaurantId,
        status: options.status,
        search: options.search,
        limit: options.limit || 200,
    })}`, scope), ['rfqs']);
};

/**
 * Load one RFQ with its items, invited suppliers and their quotes populated.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {string} rfqId RFQ `_id`.
 * @returns {Promise<object|null>}
 */
export const fetchRfq = async (scope, rfqId) => (
    (await get(`/procurement/rfqs/${encodeURIComponent(rfqId)}`, scope)) || null
);

/**
 * Create a draft RFQ for a set of suppliers.
 *
 * NOTE the supplier key is `partners` (an array of Partner ids), not
 * `partnerIds` — `createRfq` in `procurement.controller.js` maps `body.partners`
 * into the `supplierQuotes` subdocuments, so any other key silently creates an
 * RFQ nobody was invited to.
 *
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {object} payload `{items, partners, dueDate, note, warehouse}`.
 * @returns {Promise<object>}
 */
export const createRfq = async (scope, payload) => send('POST', '/procurement/rfqs', {
    restaurantId: scope?.restaurantId,
    ...payload,
}, scope);

/**
 * Update a draft RFQ.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {string} rfqId RFQ `_id`.
 * @param {object} payload Fields to change.
 * @returns {Promise<object>}
 */
export const updateRfq = async (scope, rfqId, payload) => send(
    'PUT',
    `/procurement/rfqs/${encodeURIComponent(rfqId)}`,
    payload,
    scope,
);

/**
 * Soft-delete an RFQ.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {string} rfqId RFQ `_id`.
 * @returns {Promise<object>}
 */
export const deleteRfq = async (scope, rfqId) => send(
    'DELETE',
    `/procurement/rfqs/${encodeURIComponent(rfqId)}`,
    null,
    scope,
);

/**
 * Send the RFQ to every invited supplier (marks the quotes as pending).
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {string} rfqId RFQ `_id`.
 * @returns {Promise<object>}
 */
export const sendRfq = async (scope, rfqId) => send(
    'POST',
    `/procurement/rfqs/${encodeURIComponent(rfqId)}/send`,
    {},
    scope,
);

/**
 * Record (or re-record) a supplier's quote — the prices they answered with.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {string} rfqId RFQ `_id`.
 * @param {string} partnerId The quoting supplier.
 * @param {object} payload `{lines, validUntil, currency, note, status}`.
 * @returns {Promise<object>}
 */
export const recordSupplierQuote = async (scope, rfqId, partnerId, payload) => send(
    'PUT',
    `/procurement/rfqs/${encodeURIComponent(rfqId)}/quotes/${encodeURIComponent(partnerId)}`,
    payload,
    scope,
);

/**
 * Award the RFQ to one supplier.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {string} rfqId RFQ `_id`.
 * @param {string} partnerId Winning supplier.
 * @returns {Promise<object>}
 */
export const awardRfq = async (scope, rfqId, partnerId) => send(
    'POST',
    `/procurement/rfqs/${encodeURIComponent(rfqId)}/award`,
    {partnerId},
    scope,
);

/**
 * Convert an awarded RFQ into a draft purchase order.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {string} rfqId RFQ `_id`.
 * @returns {Promise<object>} The created purchase order.
 */
export const convertRfqToOrder = async (scope, rfqId) => send(
    'POST',
    `/procurement/rfqs/${encodeURIComponent(rfqId)}/convert`,
    {},
    scope,
);

/* -------------------------------------------------------------------------- */
/*  Purchase orders (Megrendelések)                                            */
/* -------------------------------------------------------------------------- */

/**
 * List the store's purchase orders, newest first.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {{status?: string, search?: string, partnerId?: string, limit?: number}} [options] Filters.
 * @returns {Promise<Array>}
 */
export const fetchPurchaseOrders = async (scope, options = {}) => {
    if (!scope?.restaurantId) {
        return [];
    }

    return asList(await get(`/procurement/orders${qs({
        restaurantId: scope.restaurantId,
        status: options.status,
        search: options.search,
        partnerId: options.partnerId,
        limit: options.limit || 200,
    })}`, scope), ['orders', 'purchaseOrders']);
};

/**
 * Load one purchase order with partner / warehouse / approvers / items populated.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {string} orderId Purchase order `_id`.
 * @returns {Promise<object|null>}
 */
export const fetchPurchaseOrder = async (scope, orderId) => (
    (await get(`/procurement/orders/${encodeURIComponent(orderId)}`, scope)) || null
);

/**
 * Create a draft purchase order.
 * @param {{restaurantId?: string, placeId?: string}} scope Active management scope.
 * @param {object} payload `{partner, items, warehouse, expectedDeliveryDate, currency, note}`.
 * @returns {Promise<object>}
 */
export const createPurchaseOrder = async (scope, payload) => send('POST', '/procurement/orders', {
    restaurantId: scope?.restaurantId,
    ...(scope?.placeId ? {place: scope.placeId} : {}),
    ...payload,
}, scope);

/**
 * Update a draft purchase order.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {string} orderId Purchase order `_id`.
 * @param {object} payload Fields to change.
 * @returns {Promise<object>}
 */
export const updatePurchaseOrder = async (scope, orderId, payload) => send(
    'PUT',
    `/procurement/orders/${encodeURIComponent(orderId)}`,
    payload,
    scope,
);

/**
 * Soft-delete a draft purchase order.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {string} orderId Purchase order `_id`.
 * @returns {Promise<object>}
 */
export const deletePurchaseOrder = async (scope, orderId) => send(
    'DELETE',
    `/procurement/orders/${encodeURIComponent(orderId)}`,
    null,
    scope,
);

/**
 * Submit a draft order: either straight to `approved` (below the threshold) or
 * to `pending_approval` with the resolved approver list notified.
 *
 * A store whose approval threshold is set but that has NO resolvable approver
 * answers 409 (`NO_APPROVERS`) — surface the server's own message rather than a
 * generic failure.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {string} orderId Purchase order `_id`.
 * @returns {Promise<object>}
 */
export const submitPurchaseOrder = async (scope, orderId) => send(
    'POST',
    `/procurement/orders/${encodeURIComponent(orderId)}/submit`,
    {},
    scope,
);

/**
 * Record the caller's approval of a pending order.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {string} orderId Purchase order `_id`.
 * @param {string} [note] Optional approver note.
 * @returns {Promise<object>}
 */
export const approvePurchaseOrder = async (scope, orderId, note = '') => send(
    'POST',
    `/procurement/orders/${encodeURIComponent(orderId)}/approve`,
    {note},
    scope,
);

/**
 * Reject a pending order.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {string} orderId Purchase order `_id`.
 * @param {string} [note] Optional rejection reason.
 * @returns {Promise<object>}
 */
export const rejectPurchaseOrder = async (scope, orderId, note = '') => send(
    'POST',
    `/procurement/orders/${encodeURIComponent(orderId)}/reject`,
    {note},
    scope,
);

/**
 * Mark an approved order as sent to the supplier.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {string} orderId Purchase order `_id`.
 * @returns {Promise<object>}
 */
export const sendPurchaseOrder = async (scope, orderId) => send(
    'POST',
    `/procurement/orders/${encodeURIComponent(orderId)}/send`,
    {},
    scope,
);

/**
 * Prefill for the "e-mail to supplier" sheet (read-only — nothing is rendered or sent).
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {string} orderId Purchase order `_id`.
 * @param {string} [lang] Locale for the default subject/message.
 * @returns {Promise<{to: string, cc: string[], subject: string, message: string, canSend: boolean, alreadySent: boolean}|null>}
 */
export const fetchPurchaseOrderEmailPreview = async (scope, orderId, lang) => (
    (await get(
        `/procurement/orders/${encodeURIComponent(orderId)}/email-preview${lang ? `?lang=${encodeURIComponent(lang)}` : ''}`,
        scope,
    )) || null
);

/**
 * E-mail the purchase order (PDF sheet) to its supplier — `POST /procurement/orders/:id/email`.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {string} orderId Purchase order `_id`.
 * @param {{to?: string, cc?: string[], subject?: string, message?: string, lang?: string}} [body] Overrides.
 * @returns {Promise<{sentTo: string}>}
 */
export const emailPurchaseOrder = async (scope, orderId, body = {}) => send(
    'POST',
    `/procurement/orders/${encodeURIComponent(orderId)}/email`,
    body,
    scope,
);

/**
 * Cancel an order that has not been received yet.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {string} orderId Purchase order `_id`.
 * @param {string} [note] Optional cancellation reason.
 * @returns {Promise<object>}
 */
export const cancelPurchaseOrder = async (scope, orderId, note = '') => send(
    'POST',
    `/procurement/orders/${encodeURIComponent(orderId)}/cancel`,
    {note},
    scope,
);

/**
 * Close an order (accepting whatever was received/invoiced as final).
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {string} orderId Purchase order `_id`.
 * @param {string} [note] Optional closing note.
 * @returns {Promise<object>}
 */
export const closePurchaseOrder = async (scope, orderId, note = '') => send(
    'POST',
    `/procurement/orders/${encodeURIComponent(orderId)}/close`,
    {note},
    scope,
);

/**
 * Goods receipts (StockReceipt documents) booked against an order.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {string} orderId Purchase order `_id`.
 * @returns {Promise<Array>}
 */
export const fetchPurchaseOrderReceipts = async (scope, orderId) => asList(
    await get(`/procurement/orders/${encodeURIComponent(orderId)}/receipts`, scope),
    ['receipts'],
);

/**
 * Run the 3-way match of an inbound NAV invoice against the order.
 *
 * A mismatch is a normal 200 with `{matched: false, deviations}` — NOT an error:
 * the caller shows the deviations and may re-send with `force: true`.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {string} orderId Purchase order `_id`.
 * @param {string} navInvoiceId The inbound invoice to match.
 * @param {boolean} [force] Apply the match despite deviations.
 * @returns {Promise<object>} `{matched, deviations, …}`.
 */
export const matchInvoiceToOrder = async (scope, orderId, navInvoiceId, force = false) => send(
    'POST',
    `/procurement/orders/${encodeURIComponent(orderId)}/matchInvoice`,
    {navInvoiceId, force},
    scope,
);

/**
 * Reverse a previously applied invoice match.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {string} orderId Purchase order `_id`.
 * @param {string} navInvoiceId The matched invoice to detach.
 * @returns {Promise<object>}
 */
export const unmatchInvoiceFromOrder = async (scope, orderId, navInvoiceId) => send(
    'DELETE',
    `/procurement/orders/${encodeURIComponent(orderId)}/matchInvoice/${encodeURIComponent(navInvoiceId)}`,
    null,
    scope,
);

/* -------------------------------------------------------------------------- */
/*  Auto-reorder rules (Újrarendelés)                                          */
/* -------------------------------------------------------------------------- */

/**
 * List the per-product reorder rules (stock lines + their reorder config).
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {{warehouseId?: string}} [options] Optional warehouse filter.
 * @returns {Promise<Array>}
 */
export const fetchReorderRules = async (scope, options = {}) => {
    if (!scope?.restaurantId) {
        return [];
    }

    return asList(await get(`/procurement/reorderRules${qs({
        restaurantId: scope.restaurantId,
        warehouseId: options.warehouseId,
    })}`, scope), ['rules']);
};

/**
 * Update the reorder rule of one stock line.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {string} stockId Stock line `_id`.
 * @param {{reorderPoint?: number, reorderQuantity?: number, reorderPartner?: ?string, autoReorderMode?: string}} payload
 * @returns {Promise<object>}
 */
export const updateReorderRule = async (scope, stockId, payload) => send(
    'PUT',
    `/procurement/reorderRules/${encodeURIComponent(stockId)}`,
    payload,
    scope,
);

/**
 * Dry-run the low-stock sweep: which supplier buckets would be ordered, and why.
 * Creates nothing.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @returns {Promise<object>}
 */
export const previewAutoReorder = async (scope) => send(
    'POST',
    `/procurement/autoReorder/preview${qs({restaurantId: scope?.restaurantId})}`,
    {},
    scope,
);

/**
 * Run the low-stock sweep now, creating purchase orders per the configured mode.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @returns {Promise<object>}
 */
export const runAutoReorder = async (scope) => send(
    'POST',
    `/procurement/autoReorder/run${qs({restaurantId: scope?.restaurantId})}`,
    {},
    scope,
);

/* -------------------------------------------------------------------------- */
/*  Sourcing (AI beszerzési tervező)                                           */
/* -------------------------------------------------------------------------- */

/**
 * The ordering worklist: `mustOrder` (out of stock / at or below the reorder
 * point) and `suggested` (approaching it, or running out inside the supplier's
 * lead time), each row already carrying a proposed quantity, supplier and
 * estimated net value.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @returns {Promise<object|null>} `{mustOrder, suggested, totals}`.
 */
export const fetchProcurementNeeds = async (scope) => (
    scope?.restaurantId
        ? (await get(`/procurement/sourcing/needs${qs({restaurantId: scope.restaurantId})}`, scope)) || null
        : null
);

/**
 * Turn selected worklist rows into draft purchase orders or RFQs, one document
 * per supplier. Rows with no supplier come back in `unassigned` instead of being
 * silently dropped.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {Array} rows Selected need rows.
 * @param {'order'|'rfq'} target What to create.
 * @returns {Promise<object>} `{created, unassigned}`.
 */
export const convertNeeds = async (scope, rows, target) => send('POST', '/procurement/sourcing/needs/convert', {
    restaurantId: scope?.restaurantId,
    target,
    rows: (Array.isArray(rows) ? rows : []).map((row) => ({
        item: row.item,
        size: row.size || null,
        quantity: row.proposedQuantity,
        unit: row.unit,
        unitPrice: row.unitPrice,
        partner: row.partner,
        note: row.note || '',
    })),
}, scope);

/**
 * Deal watch: products the store buys regularly that are currently available
 * below their usual price, from the store's own documents (and optionally from
 * the online market sources).
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {boolean} [includeMarketPrices] Also search the online shops.
 * @returns {Promise<object|null>} `{deals, totals, market}`.
 */
export const fetchProcurementDeals = async (scope, includeMarketPrices = false) => (
    scope?.restaurantId
        ? (await get(`/procurement/sourcing/deals${qs({
            restaurantId: scope.restaurantId,
            includeMarketPrices: includeMarketPrices ? 'true' : '',
        })}`, scope)) || null
        : null
);

/**
 * Run a sourcing analysis — "where should I buy this basket?".
 *
 * Costs AI credits when `includeNarrative` is left on (the planner itself is
 * pure computation over real prices); a 403 means the store's balance is
 * exhausted, which the caller surfaces verbatim rather than as a crash.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {object} payload `{text | items, includeNarrative, includeMarketPrices, strategy, …}`.
 * @returns {Promise<object>} `{analysis, bestSupplierByItem, credits}`.
 */
export const analyzeSourcing = async (scope, payload) => send('POST', '/procurement/sourcing/analyze', {
    restaurantId: scope?.restaurantId,
    ...payload,
}, scope);

/**
 * The store's saved sourcing comparisons, newest first (list-card fields only).
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {{limit?: number}} [options] Row cap.
 * @returns {Promise<Array>}
 */
export const fetchSourcingAnalyses = async (scope, options = {}) => {
    if (!scope?.restaurantId) {
        return [];
    }

    return asList(await get(`/procurement/sourcing/analyses${qs({
        restaurantId: scope.restaurantId,
        limit: options.limit || 30,
    })}`, scope), ['analyses']);
};

/**
 * Load one saved analysis in full (items, suppliers, matrix, plans, narrative).
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {string} analysisId Analysis `_id`.
 * @returns {Promise<object|null>}
 */
export const fetchSourcingAnalysis = async (scope, analysisId) => (
    (await get(`/procurement/sourcing/analyses/${encodeURIComponent(analysisId)}`, scope)) || null
);

/**
 * Soft-delete a saved analysis.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {string} analysisId Analysis `_id`.
 * @returns {Promise<object>}
 */
export const deleteSourcingAnalysis = async (scope, analysisId) => send(
    'DELETE',
    `/procurement/sourcing/analyses/${encodeURIComponent(analysisId)}`,
    null,
    scope,
);

/**
 * Turn a plan of a saved analysis into draft purchase orders (one per orderable
 * supplier leg — webshop legs are reported, not ordered).
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {string} analysisId Analysis `_id`.
 * @param {{planKey?: string, warehouse?: string, expectedDeliveryDate?: string, note?: string}} [payload]
 * @returns {Promise<object>} `{created, skipped}`-ish payload.
 */
export const convertSourcingAnalysisToOrders = async (scope, analysisId, payload = {}) => send(
    'POST',
    `/procurement/sourcing/analyses/${encodeURIComponent(analysisId)}/orders`,
    payload,
    scope,
);

/**
 * Everything that can price a basket: the store's partners, the built-in online
 * market sources (with the store's own on/off + discount overrides) and its own
 * uploaded/scraped price lists.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @returns {Promise<object|null>} `{partners, marketSources, customSources, market, defaults}`.
 */
export const fetchSourcingSuppliers = async (scope) => (
    scope?.restaurantId
        ? (await get(`/procurement/sourcing/suppliers${qs({restaurantId: scope.restaurantId})}`, scope)) || null
        : null
);

/**
 * Supplier sourcing profiles — delivery terms (shipping fee, free-shipping
 * threshold, minimum order, lead time, delivery radius) per partner.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @returns {Promise<object|null>} `{suppliers, defaults}`.
 */
export const fetchSupplierSourcingProfiles = async (scope) => (
    scope?.restaurantId
        ? (await get(`/procurement/sourcing/supplierProfiles${qs({restaurantId: scope.restaurantId})}`, scope)) || null
        : null
);

/**
 * Save one supplier's sourcing/delivery terms.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {string} partnerId Partner `_id`.
 * @param {object} payload Whitelisted terms fields.
 * @returns {Promise<object>}
 */
export const updateSupplierSourcingProfile = async (scope, partnerId, payload) => send(
    'PUT',
    `/procurement/sourcing/supplierProfiles/${encodeURIComponent(partnerId)}${qs({restaurantId: scope?.restaurantId})}`,
    payload,
    scope,
);

/**
 * The built-in online shops available for the store's country, with the store's
 * own enabled/discount overrides and their trade sectors.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @returns {Promise<object|null>} `{market, sources, sectors, grouped, …}`.
 */
export const fetchMarketSources = async (scope) => (
    scope?.restaurantId
        ? (await get(`/procurement/sourcing/marketSources${qs({restaurantId: scope.restaurantId})}`, scope)) || null
        : null
);

/**
 * Switch a built-in online shop on/off for the store, or record the negotiated
 * discount every price of that shop is compared at.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {string} sourceKey Catalogue key (e.g. `metro.hu`).
 * @param {{enabled?: boolean, discountPct?: number, note?: string}} payload
 * @returns {Promise<object>}
 */
export const updateMarketSourcePreference = async (scope, sourceKey, payload) => send(
    'PUT',
    `/procurement/sourcing/marketSources/${encodeURIComponent(sourceKey)}${qs({restaurantId: scope?.restaurantId})}`,
    payload,
    scope,
);

/**
 * The store's own price lists (uploaded files / scraped web shops).
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {{search?: string}} [options] Optional name filter.
 * @returns {Promise<Array>}
 */
export const fetchCustomPriceSources = async (scope, options = {}) => {
    if (!scope?.restaurantId) {
        return [];
    }

    return asList(await get(`/procurement/sourcing/priceSources${qs({
        restaurantId: scope.restaurantId,
        search: options.search,
    })}`, scope), ['sources']);
};

/**
 * Create an own price source (a scrapeable URL or an empty list to fill later).
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {object} payload `{name, kind, url, currency, discountPct, …}`.
 * @returns {Promise<object>}
 */
export const createCustomPriceSource = async (scope, payload) => send('POST', '/procurement/sourcing/priceSources', {
    restaurantId: scope?.restaurantId,
    ...payload,
}, scope);

/**
 * Update an own price source.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {string} sourceId Source `_id`.
 * @param {object} payload Fields to change.
 * @returns {Promise<object>}
 */
export const updateCustomPriceSource = async (scope, sourceId, payload) => send(
    'PUT',
    `/procurement/sourcing/priceSources/${encodeURIComponent(sourceId)}${qs({restaurantId: scope?.restaurantId})}`,
    payload,
    scope,
);

/**
 * Delete an own price source.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {string} sourceId Source `_id`.
 * @returns {Promise<object>}
 */
export const deleteCustomPriceSource = async (scope, sourceId) => send(
    'DELETE',
    `/procurement/sourcing/priceSources/${encodeURIComponent(sourceId)}${qs({restaurantId: scope?.restaurantId})}`,
    null,
    scope,
);

/**
 * Re-scrape a URL-backed own price source.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {string} sourceId Source `_id`.
 * @returns {Promise<object>} `{source, imported, …}`.
 */
export const scrapeCustomPriceSource = async (scope, sourceId) => send(
    'POST',
    `/procurement/sourcing/priceSources/${encodeURIComponent(sourceId)}/scrape${qs({restaurantId: scope?.restaurantId})}`,
    {},
    scope,
);

/* -------------------------------------------------------------------------- */
/*  Catalogue lookups used by the pickers                                      */
/* -------------------------------------------------------------------------- */

/**
 * Server-side product search for the line-item editor.
 *
 * Deliberately `GET /restaurant/{id}/foods` and NOT the leaner `/foods/mobile`
 * the Products list uses: that endpoint filters
 * `foodType: {$nin: ['ingredient','ingredients']}`, i.e. it hides exactly the
 * raw materials a procurement line is usually about. This is also the endpoint
 * the admin's own procurement picker reads (`restaurantNetworking.getFoods`
 * with `basicDetails`), so both clients resolve the same catalogue.
 *
 * @param {{restaurantId?: string, placeId?: string}} scope Active management scope.
 * @param {{search?: string, page?: number, limit?: number}} [options] Query.
 * @returns {Promise<Array>} Foods with their `sizes` (never null).
 */
export const searchProcurementFoods = async (scope, options = {}) => {
    if (!scope?.restaurantId) {
        return [];
    }
    const body = await apiFetch(`/restaurant/${encodeURIComponent(scope.restaurantId)}/foods${qs({
        search: options.search,
        placeId: scope.placeId,
        page: options.page || 1,
        limit: options.limit || 30,
        basicDetails: 'true',
        showDisabled: 'true',
    })}`, {headers: scopeHeaders(scope)});

    // The endpoint answers a bare array without paging params and a
    // `{page, pages, count, data}` envelope with them — we always send paging.
    return Array.isArray(body) ? body : asList(body, ['data', 'foods']);
};

/**
 * The store's business partners (suppliers), soft-deleted ones excluded.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @returns {Promise<Array>} Partners (never null).
 */
export const fetchProcurementPartners = async (scope) => {
    const body = await apiFetch(`/partner${qs({restaurantId: scope?.restaurantId})}`, {headers: scopeHeaders(scope)});

    return asList(body, ['partners', 'docs', 'data'])
        .filter((partner) => partner && partner.enabled !== false);
};

export default {
    fetchProcurementDashboard,
    fetchRequisitions,
    fetchRequisition,
    createRequisition,
    submitRequisition,
    decideRequisition,
    convertRequisition,
    fetchRfqs,
    fetchPurchaseOrders,
    fetchProcurementNeeds,
    fetchProcurementDeals,
};
